import { useEffect, useRef, useState } from 'react';
import { WaitNote } from './wait-note.js';

/**
 * A lesson video, full screen over the page, with × to close (canvas row 9,
 * approved 16 October). For the coach, the athlete and - in 7b - the parent.
 *
 * In the app rather than a new tab, so the coach's play can mark a video
 * opened, and so a video the device cannot decode says so instead of showing a
 * black box: an iPhone's HEVC in Chrome on Windows, chiefly (said 16 October).
 * Escape closes it; focus goes to × on opening and back where it was after.
 */
export function VideoPlayer({
  url,
  label,
  failure = null,
  onClose,
}: {
  /** Null while the link is still being fetched: the player is already open. */
  url: string | null;
  label: string;
  /** Why the link could not be had, with the code, so a fault is never silent (RE-12). */
  failure?: string | null;
  onClose: () => void;
}) {
  const close = useRef<HTMLButtonElement>(null);
  const [cannot, setCannot] = useState(false);

  // The latest onClose, read by a listener set up once: a new handler on every
  // draw would re-run the focus moves and jump the page on a phone.
  const closeNow = useRef(onClose);
  closeNow.current = onClose;

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    close.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeNow.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      before?.focus();
    };
  }, []);

  return (
    <div className="video-player" role="dialog" aria-modal="true" aria-label={label}>
      <button ref={close} type="button" className="close" aria-label="Close" onClick={onClose}>
        ×
      </button>
      {failure ? (
        <p className="cannot">{failure}</p>
      ) : cannot ? (
        <p className="cannot">This video can't play on this device.</p>
      ) : url ? (
        <video src={url} controls autoPlay playsInline onError={() => setCannot(true)} />
      ) : (
        <WaitNote className="opening">Opening…</WaitNote>
      )}
    </div>
  );
}
